import { AppButton, AppTextField } from 'components';
import { Link } from 'react-router-dom';
import ChapterTopicsDropDown from 'components/molecules/ChapterTopicsDropDown';
import ClassSubjectsDropDown from 'components/molecules/ClassSubjectsDropDown';
import ESClassDropDown from 'components/molecules/ESClassDropDown';
import ESDropDown from 'components/molecules/ESDropDown';
import QuestionBankCategory from 'components/molecules/QuestionBankCategory';
import SubjectChaptersDropDown from 'components/molecules/SubjectChaptersDropDown';
import { Fragment, useEffect, useState } from 'react';
import StatusDropDown from 'components/molecules/StatusDropDown';
import PlansSelector from 'components/molecules/PlansSelector';
import Joi from 'joi';
import { checkForErrors } from 'utilities/fns';
import ESTDropDown from 'components/molecules/ESTDropDown';
import useStatus from 'hooks/useStatus';

function QuizHandleSection({ handleQuiz, preloadedData = {} }) {
  const [est, setEST] = useState(null);
  const [ES, handleES] = useState('');
  const [ESClass, handleESClass] = useState('');
  const [classSubject, handleClassSubject] = useState('');
  const [subjectChapter, handleSubjectChapter] = useState('');
  const [topic, handleTopic] = useState('');
  const [questionBank, handleQuestionBank] = useState('');
  const [quizName, handleQuizName] = useState('');
  const [quizKey, handleQuizKey] = useState('');
  const [plans, handlePlans] = useState([]);
  const [status, handleStatus] = useStatus();
  const [formErrors, setFormErrors] = useState([]);

  useEffect(() => {
    if (preloadedData?._id) {
      setEST(preloadedData?.est ?? '');
      handleES(preloadedData?.es ?? '');
      handleESClass(preloadedData?.class ?? '');
      handleClassSubject(preloadedData?.subject ?? '');
      handleSubjectChapter(preloadedData?.chapter ?? '');
      handleTopic(preloadedData?.topic ?? '');
      handleQuestionBank(preloadedData?.quizCategory ?? '');
      handleQuizName(preloadedData?.name ?? '');
      handleQuizKey(preloadedData?.key ?? '');
      handlePlans(preloadedData?.plans ?? []);
      handleStatus(preloadedData?.status ?? '');
    }
  }, [handleStatus, preloadedData]);

  function handleFormSubmission() {
    setFormErrors([]);

    const data = {
      name: quizName,
      key: quizKey,
      topic,
      class: ESClass,
      subject: classSubject,
      chapter: subjectChapter,
      plans,
      es: ES,
      est,
      status,
      quizCategory: questionBank,
    };

    const validation = manageQuizSchema.validate(data, { abortEarly: false, allowUnknown: true });

    if (validation?.error) {
      setFormErrors(validation.error?.details ?? []);
      return;
    }

    handleQuiz(data);
  }

  const errors = checkForErrors(formErrors);

  return (
    <Fragment>
      <div className="row">
        <div className="col-6">
          <h2 className="prime-head p-0">Manage Quiz</h2>
          {/* <span>Quiz Details</span> */}
        </div>
        <div className="col-6 prime-head text-right">
          <Link to="/manage/quiz">
            <AppButton variantClass="btn-plane p--tint mx-2">&#8701; Back</AppButton>
          </Link>
        </div>
      </div>

      <div className="row mt-4">
        <div className="col-lg-4 col-md-6 col-sm-12">
          {/* START: "EST" drop down */}
          <ESTDropDown
            enableAPI
            value={est}
            handleChange={(event) => setEST(event.target.value)}
            fieldErrors={errors['est']}
          />
          {/* END: "EST" drop down */}
        </div>
        <div className="col-lg-4 col-md-6 col-sm-12">
          {/* START: "ES" drop down */}
          <ESDropDown
            enableAPI={!!est}
            dependencies={{
              query: est ? `est=${est}` : ``,
            }}
            value={ES}
            handleChange={(event) => handleES(event.target.value)}
            fieldErrors={errors['es']}
            defaultLook={false}
          />
          {/* END: "ES" drop down */}
        </div>
        <div className="col-lg-4 col-md-6 col-sm-12">
          {/* START: "Class" drop down */}
          <ESClassDropDown
            enableAPI={!!ES}
            dependencies={{
              query: `esId=${ES}`,
            }}
            value={ESClass}
            fieldErrors={errors['class']}
            handleChange={(event) => handleESClass(event.target.value)}
            defaultLook={false}
          />
          {/* END: "Class" drop down */}
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-lg-4 col-md-6 col-sm-12">
          {/* START: "Subjects" drop down */}
          <ClassSubjectsDropDown
            enableAPI={!!ESClass}
            dependencies={{
              query: `esId=${ES}&classId=${ESClass}`,
            }}
            value={classSubject}
            fieldErrors={errors['subject']}
            handleChange={(event) => handleClassSubject(event.target.value)}
          />
        </div>
        {/* END: "Subjects" drop down */}
        <div className="col-lg-4 col-md-6 col-sm-12">
          {/* START: "Chapters" drop down */}
          <SubjectChaptersDropDown
            enableAPI={!!classSubject}
            dependencies={{
              classID: ESClass,
              subjectID: classSubject,
            }}
            value={subjectChapter}
            handleChange={(event) => handleSubjectChapter(event.target.value)}
            fieldErrors={errors['chapter']}
          />
          {/* END: "Chapters" drop down */}
        </div>
        <div className="col-lg-4 col-md-6 col-sm-12">
          {/* START: "Topics" drop down */}
          <ChapterTopicsDropDown
            enableAPI={!!subjectChapter}
            dependencies={{
              classID: ESClass,
              subjectID: classSubject,
              chapterID: subjectChapter,
            }}
            value={topic}
            handleChange={(event) => handleTopic(event.target.value)}
            fieldErrors={errors['topic']}
          />
          {/* END: "Topics" drop down */}
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-lg-4 col-md-6 col-sm-12">
          <PlansSelector
            enableAPI
            selectedValues={plans}
            handleChange={(plan) => {
              if (plans.includes(plan?._id)) {
                handlePlans(plans.filter((x) => x !== plan._id));
                return;
              }

              handlePlans(plans.concat(plan._id));
            }}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <AppTextField
            inputProps={{
              value: quizName,
              onChange: (event) => handleQuizName(event.target.value),
            }}
            labelProps={{ label: 'Name' }}
            fieldErrors={errors['name']}
          />
        </div>
      </div>

      <div className="row my-4">
        <div className="col-12">
          <AppTextField
            inputProps={{
              value: quizKey,
              onChange: (event) => handleQuizKey(event.target.value),
            }}
            labelProps={{ label: 'Key' }}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-lg-4 col-md-6 col-sm-12">
          <QuestionBankCategory
            value={questionBank}
            handleChange={(event) => handleQuestionBank(event.target.value)}
            fieldErrors={errors['quizCategory']}
          />
        </div>
        <div className="col-lg-4 col-md-6 col-sm-12">
          <StatusDropDown
            value={status}
            fieldErrors={errors['status']}
            handleChange={(event) => handleStatus(event.target.value)}
          />
        </div>
      </div>

      <div className="row mt-5">
        <div className="col-12 btnTab-foot">
          <AppButton variantClass="btn-right-content save" onClick={handleFormSubmission}>
            Save Changes
          </AppButton>
        </div>
      </div>
    </Fragment>
  );
}

QuizHandleSection.defaultProps = {
  handleQuiz: () => {},
};

const manageQuizSchema = Joi.object({
  est: Joi.string().required(),
  es: Joi.string().required(),
  class: Joi.string().required(),
  subject: Joi.string().required(),
  // chapter: Joi.string().required(),
  //topic: Joi.string().required(),
  name: Joi.string().required().trim(),
  quizCategory: Joi.string().required(),
  // phoneNumber: Joi.string().pattern(new RegExp('^[0-9]{10}$')).min(10).max(10).required().messages({
  //   'string.pattern.base': 'Phone number can have only numbers',
  // }),
});

export default QuizHandleSection;
